import React, { useMemo, useState } from 'react';
import Sidebar from './Sidebar';
import { FiCheck, FiX, FiDownload, FiUploadCloud } from 'react-icons/fi';

export interface FirmwareInfo {
  version?: string;
  filename?: string;
  url?: string;
}

interface FirmwareUpgradeSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  deviceId: string;
  currentVersion?: string;
  latestFirmware?: FirmwareInfo;
  onUpgrade?: (fileBlob: Blob, fileName: string) => Promise<void> | void;
}

function sanitizeUrl(url?: string): string {
  if (!url) return '';
  return url.replace(/`/g, '').trim();
}

function compareVersion(a?: string, b?: string): number {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  const pa = a.split('.').map((x) => parseInt(x, 10));
  const pb = b.split('.').map((x) => parseInt(x, 10));
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const va = pa[i] ?? 0;
    const vb = pb[i] ?? 0;
    if (va > vb) return 1;
    if (va < vb) return -1;
  }
  return 0;
}

const FirmwareUpgradeSidebar: React.FC<FirmwareUpgradeSidebarProps> = ({
  isOpen,
  onClose,
  deviceId,
  currentVersion,
  latestFirmware,
  onUpgrade,
}) => {
  const [awaitConfirm, setAwaitConfirm] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [statusText, setStatusText] = useState<string>('');

  const latestVer = latestFirmware?.version || '-';
  const latestFile = latestFirmware?.filename || '';
  const latestUrl = useMemo(() => sanitizeUrl(latestFirmware?.url), [latestFirmware?.url]);

  const isOutdated = useMemo(() => compareVersion(currentVersion, latestVer) < 0, [currentVersion, latestVer]);

  const handleRequestUpgrade = () => {
    if (!latestUrl || !latestFile) return;
    setAwaitConfirm(true);
  };

  const handleCancelConfirm = () => setAwaitConfirm(false);

  const handleConfirmUpgrade = async () => {
    if (!onUpgrade || !latestUrl || !latestFile) {
      setAwaitConfirm(false);
      return;
    }
    setIsWorking(true);
    setStatusText('Downloading firmware...');
    try {
      const resp = await fetch(latestUrl);
      if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
      const blob = await resp.blob();
      setStatusText('Uploading to device...');
      await onUpgrade(blob, latestFile);
      setStatusText('Upgrade request sent successfully');
    } catch (e: any) {
      setStatusText(e?.message || 'Upgrade failed');
    } finally {
      setIsWorking(false);
      setAwaitConfirm(false);
    }
  };

  return (
    <Sidebar isOpen={isOpen} onClose={onClose} title="Firmware Upgrade" isLoading={isWorking}>
      <div className="space-y-6">
        {/* Info card */}
        <div className="p-4 rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <h4 className="text-md font-medium text-gray-700 dark:text-gray-200 mb-4">Version Information</h4>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-gray-500 dark:text-gray-400">Current Version</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{currentVersion || '-'}</span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-gray-500 dark:text-gray-400">Latest Version</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{latestVer}</span>
          </div>
          {isOutdated ? (
            <div className="mt-3 p-3 rounded-md border border-yellow-200 bg-yellow-50 text-sm text-gray-700 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-200">
              Your firmware is outdated. Consider upgrading to {latestVer}.
            </div>
          ) : (
            <div className="mt-3 p-3 rounded-md border border-green-200 bg-green-50 text-sm text-gray-700 dark:bg-green-900/20 dark:border-green-700 dark:text-green-200">
              Your firmware is up to date.
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default FirmwareUpgradeSidebar;